import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import { BaseEntity } from '../../base/BaseEntity'
import { generateEntityId } from '../../generateEntityId'
import { CreditTransactionTypeEnum } from './CreditTransactionTypeEnum'

/**
 * A single ledger entry against a `CreditAccount` — a Debit (AI usage), Grant (admin
 * top-up), or Adjustment (manual correction). `credits` is signed; `balanceAfter`
 * snapshots the account balance immediately after this entry was applied.
 *
 * Two free-text fields, deliberately split: `remarks` (inherited from `BaseEntity`) carries the
 * description of a Debit — written by the AI call site, mirrored onto the matching
 * `AiUsageRecord`; `reason` carries the admin's free text for a Grant/Adjustment entry.
 */
@ObjectType()
@InputType('CreditTransactionInput')
@modelOptions({ schemaOptions: { timestamps: true, collection: 'aicredittransactions' } })
export class CreditTransaction extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('crtx') })
  declare _id: string

  /** Generic owner of this entry — a `User._id` (User billing scope) or a `Tenant._id` (Tenant billing scope). */
  @Field(() => String)
  @Prop({ type: String, required: true })
  ownerId: string

  @Field(() => String)
  @Prop({ type: String, required: true })
  currency: string

  @Field(() => CreditTransactionTypeEnum)
  @Prop({ type: String, enum: CreditTransactionTypeEnum, required: true })
  type: CreditTransactionTypeEnum

  /** Signed delta applied to the balance (negative for Debit, positive for Grant). */
  @Field(() => Float)
  @Prop({ type: Number, required: true })
  credits: number

  @Field(() => Float)
  @Prop({ type: Number, required: true })
  balanceAfter: number

  /** Feature tag that caused a Debit (e.g. `quickscore.processScan`). Absent for Grant/Adjustment. */
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  feature?: string

  /** Links a Debit back to the `AiUsageRecord` it was charged for. */
  @Field(() => ID, { nullable: true })
  @Prop({ type: String })
  usageRecordId?: string

  /** Free-text reason, mainly for Grant/Adjustment entries. */
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  reason?: string

  /** `_id` of the admin user who granted/adjusted this entry. Absent for system-recorded Debits. */
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  createdBy?: string

  /**
   * The `CreditPurchase._id` this Purchase entry was granted for — ledger-level idempotency
   * for the credit-grant chokepoint (a unique partial index on `(tenant._id, creditPurchaseId)`
   * for `type = Purchase` rows rejects a second grant for the same purchase). Absent for
   * Debit/Grant/Adjustment entries.
   */
  @Field(() => ID, { nullable: true })
  @Prop({ type: String })
  creditPurchaseId?: string
}
